import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Loader2, ChevronDown, ChevronRight, ClipboardList, AlertTriangle, Package, DollarSign } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { runWriteSteps } from "../../lib/write-steps";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { unwrap, unwrapArray } from "../../lib/api-envelope";
import { fmtCurrency, parseMoney, sumMoney } from "../../lib/display-labels";
import { daysUntilCalendarDate, formatCalendarDate } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { missingRateNote } from "../../lib/missing-rate-note";
import type { RentalAssetAvailability, RentalAvailabilityConflict } from "../../types/rental-availability";

/**
 * Rental Orders (/rentals/active; "Active Rentals" until 2026-10-15): the
 * rental orders out with customers.
 *
 * WHAT IT READ, AND WHAT IT READS NOW. The order list was cast to a camelCase
 * shape (`customer`, `orderId`, `well`, `daysOut`, `accrued`, `assets[]`,
 * `started`, `lastInvoice`, `coordinator`) that GET /surface-rentals/orders has
 * never sent, so every card showed an empty name, "0" days, "$0" accrued, no
 * assets and "No invoice yet". It also asked for EVERY order, so drafts and
 * closed orders sat on a page called Active Rentals. It now asks for
 * `status=active` and reads `rental_orders.*` + customer_name, is_overdue and
 * asset_count. The assets, dispatches and invoices of one order come from
 * GET /orders/:id when the order is opened, and what an invoice would charge
 * from GET /billing/calculate/:id.
 *
 * MONEY IS THE API'S, ALL OR NOTHING (api#854, #815). GET
 * /billing/dashboard-summary sends `daily_revenue`, `accrued_this_month` and
 * `open_ar`, each null when a rate or balance behind it is missing, with a
 * count saying how many; the billing preview nulls a line, the subtotal, every
 * tax amount and the total the same way. A null is a dash with that count
 * beside it, never a partial sum, and nothing here is re-summed.
 *
 * "Accrued this month" is what the API sends in place of `mtd_revenue`, which
 * was not month-to-date revenue (it counted only rentals that went ON rent this
 * month). It is days on rent this month times the rate: an accrual, not
 * invoiced revenue, and labelled so.
 *
 * RESERVED ORDERS ARE THEIR OWN LIST (api#1091). An order whose start date is
 * after the tenant's today reads `effective_status: 'reserved'`, and
 * `?status=active` means on rent TODAY, so the register below no longer lists
 * it. It counted as "out with customers" and read "0 days" on rent before. The
 * Reserved panel asks `?status=reserved` and shows the API's rows as sent.
 *
 * GONE: the "Ending soon" filter
 * (it matched orders out FEWER than 14 days, the opposite of ending), "GST (5%)"
 * printed for every jurisdiction (the preview carries the tax it applied), the
 * Export button (no handler) and "View Order" (/rentals/orders/:id is not a
 * route; the order now opens here).
 */

interface OrderRow {
  id: string;
  order_number: string | null;
  customer_name: string | null;
  customer_id: string | null;
  well_name: string | null;
  lsd: string | null;
  start_date: string | null;
  expected_return: string | null;
  is_overdue: boolean;
  asset_count: number | null;
  afe_number: string | null;
  po_number: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
function toOrder(r: Record<string, unknown>): OrderRow {
  return {
    id: String(r.id),
    order_number: str(r.order_number),
    customer_name: str(r.customer_name),
    customer_id: str(r.customer_id),
    well_name: str(r.well_name),
    lsd: str(r.lsd),
    start_date: str(r.start_date),
    expected_return: str(r.expected_return),
    is_overdue: r.is_overdue === true,
    asset_count: parseMoney(r.asset_count),
    afe_number: str(r.afe_number),
    po_number: str(r.po_number),
  };
}

/** Days since the order's start day (a calendar day), counted from the TENANT's today, or null. 0 is the day it started. */
const daysOnRent = (o: OrderRow, timeZone: string | null) => {
  const d = daysUntilCalendarDate(o.start_date, timeZone);
  return d == null ? null : Math.max(0, -d);
};
/** Days until the expected return day, from the TENANT's today; negative is past. */
const daysToReturn = (o: OrderRow, timeZone: string | null) => daysUntilCalendarDate(o.expected_return, timeZone);

type ChipKey = "all" | "overdue" | "week" | "long";
/**
 * Tallies of the loaded list: GET /orders is unpaginated and no endpoint
 * returns these counts.
 */
const CHIPS: { key: ChipKey; label: string; match: (o: OrderRow, timeZone: string | null) => boolean }[] = [
  { key: "all", label: "All", match: () => true },
  { key: "overdue", label: "Overdue", match: (o) => o.is_overdue },
  { key: "week", label: "Due back this week", match: (o, tz) => { const d = daysToReturn(o, tz); return !o.is_overdue && d != null && d >= 0 && d <= 7; } },
  { key: "long", label: "Out 30+ days", match: (o, tz) => (daysOnRent(o, tz) ?? -1) >= 30 },
];

const ASSETS = { one: "asset", many: "assets" };
const fmtDay = (d: string | null | undefined) => formatCalendarDate(d ?? null, { month: "short", day: "numeric", year: "numeric" }) ?? "—";
const badge = "inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink";
const eyebrowCls = "text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted";
const selectCls = "h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

const BILLING_FREQUENCY_OPTIONS = [
  { value: "per_event", label: "Per Event" },
  { value: "per_haul", label: "Per Haul" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "per_well", label: "Per Well" },
];

/** An available asset as GET /assets sends it: `asset_number` and `daily_rate`, not `tag` / `rate`. */
interface AvailableAsset { id: string; name: string; asset_number: string; daily_rate: number | null }

function toAvailableAsset(r: Record<string, unknown>): AvailableAsset {
  return {
    id: String(r.id ?? ""),
    name: String(r.name ?? "—"),
    asset_number: String(r.asset_number ?? ""),
    daily_rate: parseMoney(r.daily_rate),
  };
}

/**
 * The order's billing trigger. Blank sends nothing, and the API then takes it
 * from each asset's price book (or arrival). Exactly BILLING_TRIGGERS in the
 * API's rental-billing.ts; the wizard used to offer `on_arrival` and
 * `spud_date`, which it rejects.
 */
const ORDER_TRIGGERS = [
  { value: "arrival", label: "On Arrival" },
  { value: "rig_release", label: "Rig Release" },
  { value: "spud", label: "Spud" },
  { value: "custom", label: "Custom" },
];

/** One selected asset's availability, as the API answered it for one start date. */
type AvailabilityState =
  | { status: "loading" }
  | { status: "ok"; conflicts: RentalAvailabilityConflict[] }
  | { status: "error"; message: string };

/** Under an asset in the wizard: the API's conflict sentences, one line each. Nothing when it is free. */
function AvailabilityNote({ state }: { state: AvailabilityState | undefined }) {
  if (!state) return null;
  if (state.status === "loading") return <p className="text-xs text-fieldiq-muted">Checking availability…</p>;
  if (state.status === "error") return <p role="status" className="text-xs text-fieldiq-text">{state.message}</p>;
  if (state.conflicts.length === 0) return null;
  return (
    <ul data-testid="availability-conflicts" className="space-y-0.5">
      {state.conflicts.map((c, i) => (
        <li key={i} className="flex items-start gap-1.5 text-xs font-medium text-fieldiq-text">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fi-amber" aria-hidden="true" />
          {c.message}
        </li>
      ))}
    </ul>
  );
}

const ActiveRentals = () => {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [chip, setChip] = useState<ChipKey>("all");
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");

  // New Rental Order
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [availableAssets, setAvailableAssets] = useState<AvailableAsset[]>([]);
  const [newOrderLoadError, setNewOrderLoadError] = useState<string | null>(null);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newOrderStep, setNewOrderStep] = useState(1);
  const [newOrderNumber, setNewOrderNumber] = useState("");
  /** What GET /orders/next-number proposed. Reserved by nothing, so it is sent only if the user changed it. */
  const [proposedOrderNumber, setProposedOrderNumber] = useState("");
  /** Per selected asset: the API's availability answer for the order's start date onward. */
  const [availability, setAvailability] = useState<Record<string, AvailabilityState>>({});
  const [newOrderStartDate, setNewOrderStartDate] = useState("");
  const [newOrderCustomer, setNewOrderCustomer] = useState("");
  const [newOrderWell, setNewOrderWell] = useState("");
  const [newOrderLsd, setNewOrderLsd] = useState("");
  const [newOrderAfe, setNewOrderAfe] = useState("");
  const [newOrderBillingTrigger, setNewOrderBillingTrigger] = useState("");
  const [newOrderAssetSelection, setNewOrderAssetSelection] = useState<Record<string, boolean>>({});
  /** A negotiated $/day per asset, as typed. Blank = no order-line rate (the price book or asset rate applies). */
  const [newOrderRates, setNewOrderRates] = useState<Record<string, string>>({});
  const [newOrderBillingFrequencyOverride, setNewOrderBillingFrequencyOverride] = useState("");
  /** Steps saved by an earlier Confirm, and the order they created, so a retry adds only what is missing. */
  const newOrderSaved = useRef(new Set<string>());
  const newOrderId = useRef<string | null>(null);
  /** The number the API gave the order it created, for the success line. */
  const newOrderCreatedNumber = useRef<string | null>(null);
  const [newOrderShowAdvanced, setNewOrderShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const ordersQuery = useQuery({
    queryKey: ["rentals-orders", "active"],
    queryFn: () => api.rentals.orders.list({ status: "active" }).then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toOrder)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(ordersQuery) != null;
  const loading = ordersQuery.isLoading && !failed;
  const orders = failed ? undefined : ordersQuery.data;

  // Upcoming orders (api#1091): committed, not yet on rent.
  const reservedQuery = useQuery({
    queryKey: ["rentals-orders", "reserved"],
    queryFn: () => api.rentals.orders.list({ status: "reserved" }).then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toOrder)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const reservedFailed = queryLoadFailure(reservedQuery) != null;
  const reserved = reservedFailed ? undefined : reservedQuery.data;

  // Day rate, accrued this month and open AR: the API's figures, each null
  // when anything behind it is missing (api#854). Not re-summed here.
  const summaryQuery = useQuery({
    queryKey: ["rentals-billing-summary"],
    queryFn: () => api.rentals.billing.dashboardSummary().then((r) => unwrap<Record<string, unknown>>(r.data) ?? {}),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const summaryFailed = queryLoadFailure(summaryQuery) != null;
  const summaryLoading = summaryQuery.isLoading && !summaryFailed;
  const summary = summaryFailed ? undefined : summaryQuery.data;
  /** A summary money figure: the API's value, or a dash with its count. */
  const summaryFigure = (key: string, countKey: string, noun: { one: string; many: string }, predicate: string) => {
    const value = summary ? parseMoney(summary[key]) : null;
    return {
      value: summaryFailed || !summary ? "—" : fmtCurrency(value),
      note: summary && value === null ? missingRateNote(summary[countKey], noun, predicate) : null,
    };
  };
  const dayRate = summaryFigure("daily_revenue", "daily_unrated_count", ASSETS, "no rate");
  const accrued = summaryFigure("accrued_this_month", "accrued_unrated_count", ASSETS, "no rate");
  const openAr = summaryFigure("open_ar", "open_ar_unknown_count", { one: "open invoice", many: "open invoices" }, "no balance on file");

  const countOf = (k: ChipKey): number | null => {
    const c = CHIPS.find((x) => x.key === k)!;
    return orders ? orders.filter((o) => c.match(o, timeZone)).length : null;
  };
  const tally = (n: number | null) => (failed ? "—" : displayValue(n));
  const assetsOut = orders ? sumMoney(orders.map((o) => o.asset_count)) : null;

  const customerNames = useMemo(
    () => Array.from(new Set((orders ?? []).map((o) => o.customer_name).filter((n): n is string => !!n))).sort(),
    [orders],
  );

  const filtered = useMemo(() => {
    const c = CHIPS.find((x) => x.key === chip)!;
    let rows = (orders ?? []).filter((o) => c.match(o, timeZone));
    if (customerFilter) rows = rows.filter((o) => o.customer_name === customerFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((o) => [o.order_number, o.customer_name, o.well_name, o.lsd].some((v) => (v ?? "").toLowerCase().includes(q)));
    }
    return rows;
  }, [orders, chip, customerFilter, search, timeZone]);

  // A row opens the order's own page (#815 PR D): its record, assets, billing
  // preview, invoices, Generate and Close. It was a panel beside the register.
  const select = (id: string) => navigate(`/rentals/orders/${encodeURIComponent(id)}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select(id);
    }
  };

  // --- New Rental Order ---
  const resetNewOrder = () => {
    setNewOrderStep(1);
    setNewOrderNumber("");
    setNewOrderStartDate("");
    setNewOrderCustomer("");
    setNewOrderWell("");
    setNewOrderLsd("");
    setNewOrderAfe("");
    setNewOrderBillingTrigger("");
    setNewOrderAssetSelection({});
    setNewOrderRates({});
    setNewOrderBillingFrequencyOverride("");
    setNewOrderShowAdvanced(false);
    setProposedOrderNumber("");
    setAvailability({});
    checkedFrom.current = "";
    newOrderSaved.current = new Set();
    newOrderId.current = null;
    newOrderCreatedNumber.current = null;
  };
  const openNewOrder = async () => {
    resetNewOrder();
    setNewOrderLoadError(null);
    setNewOrderOpen(true);
    // The proposal is a convenience: if it fails, the field stays blank and the
    // server numbers the order on save, so nothing is said about it.
    api.rentals.orders
      .nextNumber()
      .then((r) => {
        const n = unwrap<{ order_number?: string }>(r.data)?.order_number;
        if (n) {
          setProposedOrderNumber(n);
          setNewOrderNumber((cur) => (cur === "" ? n : cur));
        }
      })
      .catch(() => undefined);
    try {
      const [c, a] = await Promise.all([api.customers.list(), api.rentals.assets.list({ status: "available" })]);
      setCustomers(unwrapArray(c.data));
      setAvailableAssets(unwrapArray<Record<string, unknown>>(a.data).map(toAvailableAsset));
    } catch (err) {
      // Not "silent - will show empty dropdowns": an empty picker would read
      // as "no customers" or "nothing available".
      setNewOrderLoadError(getApiErrorMessage(err, "Couldn't load customers and available equipment."));
    }
  };
  // "+ New Rental Order" on the Overview (and the sidebar) lands here with
  // ?new=1: open the wizard once, then drop the param so a refresh or Back
  // does not open it again.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromLink = useRef(false);
  useEffect(() => {
    if (searchParams.get("new") !== "1" || openedFromLink.current) return;
    openedFromLink.current = true;
    void openNewOrder();
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openNewOrder is a new function each render; the ref keeps this to once
  }, [searchParams]);

  // The order number is optional (api#940): blank, the server numbers it.
  const step1Missing = !newOrderStartDate || !newOrderCustomer;

  /**
   * Ask the API whether each selected asset is free from the order's start
   * date onward (api#966; the wizard sets no return date, so open-ended).
   * Shown under the asset before Confirm, in the API's own words. It does not
   * block Confirm: the API refuses a double booking with a 409 either way, and
   * that 409 is shown as sent.
   */
  const checkAvailability = async (assetId: string, from: string) => {
    setAvailability((prev) => ({ ...prev, [assetId]: { status: "loading" } }));
    try {
      const res = await api.rentals.assets.availability(assetId, { from });
      const data = unwrap<RentalAssetAvailability>(res.data);
      setAvailability((prev) => ({
        ...prev,
        [assetId]: data ? { status: "ok", conflicts: data.conflicts ?? [] } : { status: "error", message: "No answer from the availability check." },
      }));
    } catch (err) {
      setAvailability((prev) => ({ ...prev, [assetId]: { status: "error", message: getApiErrorMessage(err, "Couldn't check availability.") } }));
    }
  };

  /**
   * POST /orders requires `order_number`, `customer_id` and `start_date`
   * (#815: the wizard sent none of the first and third, so every confirm was a
   * 400). The well goes as `well_name`; blank fields are omitted, never "TBD"
   * or a made-up LSD. The order is created `active`, because this page lists
   * active orders and an order made here is one equipment is going out on.
   * Then each asset is added with ITS OWN request (the route takes one
   * `asset_id`; `{asset_ids: []}` was a 400), with a negotiated rate only when
   * one was typed. The order's "partial day rule" and "rates" were dropped by
   * the API, so the wizard no longer asks for them.
   */
  const handleNewOrderConfirm = async () => {
    const orderNumber = newOrderNumber.trim();
    // Sent only when the user typed one: the proposal is a peek, and the server
    // assigns the next number itself when the key is absent (api#940).
    const sendNumber = orderNumber !== "" && orderNumber !== proposedOrderNumber;
    const rates: Record<string, number> = {};
    for (const a of selectedAssetObjects) {
      const text = (newOrderRates[a.id] ?? "").trim();
      if (text === "") continue;
      const n = Number(text);
      // A typed 0 is a real $0 rate; blank is "no negotiated rate".
      if (!Number.isFinite(n) || n < 0) {
        toast.error(`The rate for ${a.asset_number || a.name} must be $0 or more, or left blank.`);
        return;
      }
      rates[a.id] = n;
    }
    setSubmitting(true);
    try {
      // No client-side duplicate check: the API answers a used number with a
      // 409 naming it (api#864), shown as sent.
      const body: Record<string, unknown> = {
        ...(sendNumber ? { order_number: orderNumber } : {}),
        customer_id: newOrderCustomer,
        start_date: newOrderStartDate,
        status: "active",
      };
      if (newOrderWell.trim()) body.well_name = newOrderWell.trim();
      if (newOrderLsd.trim()) body.lsd = newOrderLsd.trim();
      if (newOrderAfe.trim()) body.afe_number = newOrderAfe.trim();
      if (newOrderBillingTrigger) body.billing_trigger = newOrderBillingTrigger;
      if (newOrderBillingFrequencyOverride) body.billing_frequency_override = newOrderBillingFrequencyOverride;

      await runWriteSteps(
        [
          {
            label: "the order",
            run: async () => {
              const res = await api.rentals.orders.create(body);
              const row = unwrap<Record<string, unknown>>(res.data);
              if (!row?.id) throw new Error("the API returned no order id");
              newOrderId.current = String(row.id);
              newOrderCreatedNumber.current = row.order_number ? String(row.order_number) : null;
            },
          },
          ...selectedAssetObjects.map((a) => ({
            label: `${a.asset_number || a.name} on the order`,
            run: () =>
              api.rentals.orders.addAsset(newOrderId.current!, {
                asset_id: a.id,
                ...(rates[a.id] !== undefined ? { applied_rate: rates[a.id] } : {}),
              }),
          })),
        ],
        newOrderSaved.current,
      );
      const createdNumber = newOrderCreatedNumber.current;
      setNewOrderOpen(false);
      resetNewOrder();
      toast.success(createdNumber ? `Rental order ${createdNumber} created` : "Rental order created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : getApiErrorMessage(err, "Failed to create rental order"));
    } finally {
      setSubmitting(false);
      queryClient.invalidateQueries({ queryKey: ["rentals-orders"] });
    }
  };

  const selectedAssetIds = Object.entries(newOrderAssetSelection).filter(([, s]) => s).map(([id]) => id);
  const selectedAssetObjects = availableAssets.filter((a) => selectedAssetIds.includes(a.id));

  // Each selected asset is checked from the start date onward; a new start
  // date re-checks them all. Unselecting drops the answer.
  const checkedFrom = useRef<string>("");
  const selectedKey = selectedAssetIds.join(",");
  useEffect(() => {
    if (!newOrderOpen || !newOrderStartDate) return;
    if (checkedFrom.current !== newOrderStartDate) {
      checkedFrom.current = newOrderStartDate;
      setAvailability({});
      selectedAssetIds.forEach((id) => void checkAvailability(id, newOrderStartDate));
      return;
    }
    setAvailability((prev) => {
      const next: Record<string, AvailabilityState> = {};
      for (const id of selectedAssetIds) if (prev[id]) next[id] = prev[id];
      return next;
    });
    selectedAssetIds.filter((id) => !availability[id]).forEach((id) => void checkAvailability(id, newOrderStartDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the selection and the start date
  }, [selectedKey, newOrderStartDate, newOrderOpen]);

  return (
    <div data-testid="active-rentals-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Rental Orders"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button
            type="button"
            onClick={openNewOrder}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New Rental Order
          </button>
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: "Orders", sub: "Active now", tone: "neutral", loading, value: tally(countOf("all")) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: "Overdue", sub: "Past return date", tone: "accent", loading, value: tally(countOf("overdue")) },
          { icon: <Package className="size-4" aria-hidden="true" />, label: "Assets Out", sub: "On orders", tone: "info", loading, value: failed ? "—" : displayValue(assetsOut) },
          { icon: <DollarSign className="size-4" aria-hidden="true" />, label: "Day Rate", sub: "Assets on rent", tone: "success", loading: summaryLoading, value: dayRate.value },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <section aria-labelledby="billing-summary-title" data-testid="billing-summary" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
            <h2 id="billing-summary-title" className={eyebrowCls}>Billing, all open orders</h2>
            {summaryFailed ? (
              <LoadFailureNotice error={queryFailureError(summaryQuery)} subject="the billing summary" allowed="admins, office staff, supervisors and dispatchers" />
            ) : (
              <dl className="mt-2 grid gap-3 sm:grid-cols-3">
                {[
                  { label: "Day rate", hint: "Assets on rent now, per day", fig: dayRate, testid: "summary-day-rate" },
                  { label: "Accrued this month", hint: "Days on rent since the 1st × rate. Not yet invoiced.", fig: accrued, testid: "summary-accrued" },
                  { label: "Open AR", hint: "Unpaid rental invoices", fig: openAr, testid: "summary-open-ar" },
                ].map((f) => (
                  <div key={f.label} data-testid={f.testid}>
                    <dt className="text-xs font-semibold text-fieldiq-text">{f.label}</dt>
                    <dd className="mt-0.5 text-lg font-extrabold tabular-nums text-fieldiq-text">{summaryLoading ? "" : f.fig.value}</dd>
                    {f.fig.note && <dd className="text-xs text-fieldiq-text">{f.fig.note}</dd>}
                    <dd className="text-[11px] text-fieldiq-muted">{f.hint}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {CHIPS.map((c) => {
                const active = chip === c.key;
                const n = countOf(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChip(c.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active ? "bg-fi-amber text-fi-amber-ink" : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {c.label}
                    {!loading && !failed && n != null && <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select aria-label="Filter by customer" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} className={selectCls}>
                <option value="">All customers</option>
                {customerNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
                <input
                  placeholder="Search order, customer, well, LSD..."
                  aria-label="Search active rentals"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div>
            <section aria-labelledby="active-rentals-title" className="min-w-0 overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className={eyebrowCls}>Rental order register</p>
                <h2 id="active-rentals-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Out with customers</h2>
              </header>
              {failed ? (
                <LoadFailureNotice error={queryFailureError(ordersQuery)} subject="rental orders" />
              ) : loading ? (
                <div className="space-y-2 p-4" aria-hidden="true">
                  {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />)}
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                  {(orders ?? []).length === 0 ? "No active rental orders" : "No orders match these filters"}
                </p>
              ) : (
                <>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[820px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                          <th className="px-4 py-3">Order</th>
                          <th className="px-4 py-3">Customer</th>
                          <th className="px-4 py-3">Site</th>
                          <th className="px-4 py-3">On rent since</th>
                          <th className="px-4 py-3">Expected return</th>
                          <th className="px-4 py-3 text-right">Assets</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((o) => {
                          const days = daysOnRent(o, timeZone);
                          return (
                            <tr
                              key={o.id}
                              data-testid="active-rental-row"
                              role="button"
                              tabIndex={0}
                              onClick={() => select(o.id)}
                              onKeyDown={rowKeys(o.id)}
                              className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${o.is_overdue ? "border-l-fi-red" : "border-l-fi-green"}`}
                            >
                              <td className="px-4 py-3 font-mono text-sm font-bold text-fieldiq-text">{o.order_number ?? "—"}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">{o.customer_name ?? "—"}</td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-fieldiq-text">{o.well_name ?? "—"}</p>
                                <p className="font-mono text-[11px] text-fieldiq-muted">{o.lsd ?? "—"}</p>
                              </td>
                              <td className="px-4 py-3 text-xs text-fieldiq-muted">
                                {fmtDay(o.start_date)}
                                <p className="text-fieldiq-text">{days == null ? "—" : `${days} days`}</p>
                              </td>
                              <td className="px-4 py-3 text-xs text-fieldiq-muted">
                                {fmtDay(o.expected_return)}
                                {o.is_overdue && <span className={`${badge} ml-2 bg-fi-signal-red`}>Overdue</span>}
                              </td>
                              <td className="px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">{displayValue(o.asset_count)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-3 p-4 md:hidden">
                    {filtered.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        data-testid="active-rental-card"
                        onClick={() => select(o.id)}
                        className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-extrabold text-fieldiq-text">{o.customer_name ?? "—"}</p>
                            <p className="font-mono text-xs text-fieldiq-muted">{o.order_number ?? "—"}</p>
                          </div>
                          {o.is_overdue && <span className={`${badge} bg-fi-signal-red`}>Overdue</span>}
                        </div>
                        <p className="mt-2 text-xs text-fieldiq-muted">
                          {o.well_name ?? "—"} · back {fmtDay(o.expected_return)} · {displayValue(o.asset_count)} assets
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                    Showing {filtered.length} of {(orders ?? []).length} active orders
                  </p>
                </>
              )}
            </section>

            {/* Reserved: drawn once the API has answered, and only when it said something. */}
            {(reservedFailed || (reserved && reserved.length > 0)) && (
              <section aria-labelledby="reserved-rentals-title" data-testid="reserved-rentals" className="mt-4 min-w-0 overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className={eyebrowCls}>Starting soon</p>
                  <h2 id="reserved-rentals-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Reserved, not yet on rent</h2>
                </header>
                {reservedFailed ? (
                  <LoadFailureNotice error={queryFailureError(reservedQuery)} subject="reserved rental orders" />
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {(reserved ?? []).map((o) => (
                      <li key={o.id} className="border-l-4 border-l-fi-blue">
                        <button
                          type="button"
                          data-testid="reserved-rental-row"
                          onClick={() => select(o.id)}
                          className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 text-left outline-none hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-fieldiq-text">
                              <span className="font-mono">{o.order_number ?? "—"}</span> · {o.customer_name ?? "—"}
                            </span>
                            <span className="block text-xs text-fieldiq-muted">{o.well_name ?? "—"}</span>
                          </span>
                          <span className="flex items-center gap-3 text-xs text-fieldiq-muted">
                            <span className={`${badge} bg-fi-signal-blue`}>Reserved</span>
                            <span>Starts <span className="text-fieldiq-text">{fmtDay(o.start_date)}</span></span>
                            <span>back {fmtDay(o.expected_return)}</span>
                            <span className="tabular-nums text-fieldiq-text">{displayValue(o.asset_count)} {o.asset_count === 1 ? "asset" : "assets"}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
        </div>
      </section>

      {/* ========== NEW RENTAL ORDER (switched off; kept for the #815 repair) ========== */}
      <Dialog open={newOrderOpen} onOpenChange={setNewOrderOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New Rental Order — Step {newOrderStep} of 3</DialogTitle>
          </DialogHeader>
          {newOrderLoadError && (
            <p role="alert" className="text-sm text-fieldiq-text">{newOrderLoadError}</p>
          )}
          {newOrderStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-order-number">Order Number</Label>
                  <Input id="new-order-number" maxLength={100} value={newOrderNumber} onChange={(e) => setNewOrderNumber(e.target.value)} />
                  <p className="mt-1 text-xs text-fieldiq-muted">Leave blank to number it automatically.</p>
                </div>
                <div>
                  <Label htmlFor="new-order-start">Start Date *</Label>
                  <Input id="new-order-start" type="date" value={newOrderStartDate} onChange={(e) => setNewOrderStartDate(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="new-order-customer">Customer *</Label>
                <select id="new-order-customer" value={newOrderCustomer} onChange={(e) => setNewOrderCustomer(e.target.value)} className={`${selectCls} w-full`}>
                  <option value="">Select customer...</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="new-order-well">Well / Site Name</Label>
                <Input id="new-order-well" value={newOrderWell} onChange={(e) => setNewOrderWell(e.target.value)} placeholder="e.g. Karr 16-23-63-5W6" />
              </div>
              <div>
                <Label htmlFor="new-order-lsd">LSD</Label>
                <Input id="new-order-lsd" maxLength={100} value={newOrderLsd} onChange={(e) => setNewOrderLsd(e.target.value)} placeholder="e.g. 10-23-063-05" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-order-afe">AFE Number</Label>
                  <Input id="new-order-afe" maxLength={100} value={newOrderAfe} onChange={(e) => setNewOrderAfe(e.target.value)} placeholder="AFE-00000" />
                </div>
                <div>
                  <Label htmlFor="new-order-trigger">Billing Trigger</Label>
                  <select id="new-order-trigger" value={newOrderBillingTrigger} onChange={(e) => setNewOrderBillingTrigger(e.target.value)} className={`${selectCls} w-full`}>
                    <option value="">From each asset&rsquo;s price book</option>
                    {ORDER_TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-t border-fieldiq-border pt-3">
                <button type="button" className="flex items-center gap-1.5 text-xs font-medium text-fieldiq-muted hover:text-fieldiq-text" onClick={() => setNewOrderShowAdvanced(!newOrderShowAdvanced)}>
                  {newOrderShowAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Advanced
                </button>
                {newOrderShowAdvanced && (
                  <div className="mt-3">
                    <Label htmlFor="new-order-frequency" className="text-xs text-fieldiq-muted">Billing Frequency Override</Label>
                    <select id="new-order-frequency" value={newOrderBillingFrequencyOverride} onChange={(e) => setNewOrderBillingFrequencyOverride(e.target.value)} className={`${selectCls} mt-1 w-full`}>
                      <option value="">Use customer default</option>
                      {BILLING_FREQUENCY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
          {newOrderStep === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-fieldiq-muted">Select assets for this rental order:</p>
              {availableAssets.length === 0 && <p className="py-4 text-center text-sm text-fieldiq-muted">No available assets found.</p>}
              {availableAssets.map((asset) => (
                <div key={asset.id} className="flex items-center justify-between rounded-fi-control border border-fieldiq-border p-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      aria-label={`Add ${asset.asset_number || asset.name}`}
                      checked={!!newOrderAssetSelection[asset.id]}
                      onCheckedChange={(checked) => setNewOrderAssetSelection((prev) => ({ ...prev, [asset.id]: checked as boolean }))}
                    />
                    <div className="min-w-0">
                      <span className="text-sm text-fieldiq-text">{asset.name}</span>
                      <p className="font-mono text-xs text-fieldiq-muted">{asset.asset_number || "—"}</p>
                      {newOrderAssetSelection[asset.id] && <AvailabilityNote state={availability[asset.id]} />}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-fieldiq-text">{asset.daily_rate == null ? "No rate" : `${fmtCurrency(asset.daily_rate)}/day`}</span>
                </div>
              ))}
            </div>
          )}
          {newOrderStep === 3 && (
            <div className="space-y-4">
              <p className="mb-2 text-sm text-fieldiq-muted">
                A negotiated day rate for this order, if there is one. Leave it blank and the price book or the asset&rsquo;s own rate applies.
              </p>
              {selectedAssetObjects.length === 0 && <p className="text-sm text-fieldiq-muted">No assets selected. The order is created without any.</p>}
              {selectedAssetObjects.map((asset) => (
                <div key={asset.id} className="flex items-center justify-between rounded-fi-control border border-fieldiq-border p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fieldiq-text">{asset.name}</p>
                    <p className="font-mono text-xs text-fieldiq-muted">
                      {asset.asset_number || "—"} · {asset.daily_rate == null ? "no asset rate" : `asset rate ${fmtCurrency(asset.daily_rate)}/day`}
                    </p>
                    <AvailabilityNote state={availability[asset.id]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`rate-${asset.id}`} className="text-xs text-fieldiq-muted">$/day</Label>
                    <Input
                      id={`rate-${asset.id}`}
                      type="number"
                      min="0"
                      className="h-8 w-24 text-sm"
                      value={newOrderRates[asset.id] ?? ""}
                      onChange={(e) => setNewOrderRates((prev) => ({ ...prev, [asset.id]: e.target.value }))}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {newOrderStep > 1 && <Button variant="outline" onClick={() => setNewOrderStep((s) => s - 1)} disabled={submitting}>Back</Button>}
            {newOrderStep < 3 ? (
              <Button onClick={() => setNewOrderStep((s) => s + 1)} disabled={newOrderStep === 1 && step1Missing}>Next</Button>
            ) : (
              <Button onClick={handleNewOrderConfirm} disabled={submitting}>
                {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Confirm
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ActiveRentals;
